import type { FormEvent } from 'react'
import React, { useState } from 'react'
import {
  Button,
  FormField,
  InputV2,
  Link,
  Picture,
  TypographyV2 as Typography,
} from '@which/seatbelt'
import type { ImageWithSources } from '@which/shared'

import { useFeatureIsOn } from '@growthbook/growthbook-react'

import styles from './BroadbandSwitchCTA.module.scss'
import { amazonCardImage, defaultImage } from './constants'

interface Props {
  title?: string
  description?: string
  offerText?: string
  smallPrint?: { text: string; link: string }
  image?: ImageWithSources
}

export const offer = {
  title: 'Switch broadband, get a gift card',
  image: amazonCardImage,
  offerText: 'Switch via Which? and get a £25 Amazon.co.uk gift card.',
  cta: 'Compare and switch',
  smallPrint: {
    text: 'Offer ends 5 November 2025, gift card emailed 90 days after offer ends, T&Cs apply.',
    link: null,
  },
}

export const BroadbandSwitchCTA: React.FC<Props> = ({
  title = 'Compare broadband deals',
  description = 'Use Which? to search for faster, more reliable broadband services.',
  image = defaultImage,
}) => {
  const isOctoberOffer = useFeatureIsOn('whc-1081-october-2025-offer')
  const [postcode, setPostcode] = useState<string>('')
  const imageData = isOctoberOffer ? offer.image : image
  const link = isOctoberOffer
    ? `https://broadband.which.co.uk/broadband-deals?postcode=${postcode}&utm_source=which&utm_medium=advice&utm_campaign=bb_offer_oct25`
    : `https://broadband.which.co.uk/broadband-deals?postcode=${postcode}&utm_source=which&utm_medium=advice&utm_campaign=postcodetool`

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    window.open(link, '_self')
  }

  return (
    <aside aria-labelledby="broadband-cta-heading" className={styles.container}>
      <div className={styles.content}>
        <Typography
          className={styles.heading}
          id="broadband-cta-heading"
          textStyle="sb-text-heading-small"
        >
          {isOctoberOffer ? offer.title : title}
        </Typography>
        <Typography textStyle="sb-text-body-default-regular">{description}</Typography>
        {isOctoberOffer && (
          <Typography data-testid="offer-text" textStyle="sb-text-body-default-strong">
            {offer.offerText}
          </Typography>
        )}
        <form
          className={styles.form}
          data-which-id="compare-broadband-widget-form"
          name="broadband-switch"
          onSubmit={handleSubmit}
          title={title}
        >
          <FormField
            label="Postcode"
            labelFor="broadband-switch-postcode"
            showRequiredText={false}
            className={styles.postcodeField}
          >
            <InputV2
              id="broadband-switch-postcode"
              name="broadband-switch-postcode"
              placeholder="eg. NW1 4DF"
              value={postcode}
              onChange={(event) => setPostcode(event.target.value)}
            />
          </FormField>
          <Button type="submit">{isOctoberOffer ? offer.cta : 'Compare'}</Button>
          {isOctoberOffer && (
            <Typography
              className={styles.smallPrint}
              data-testid="small-print-terms-conditions"
              textStyle="sb-text-body-x-small-regular"
            >
              {offer.smallPrint.text}
            </Typography>
          )}
        </form>
      </div>
      {imageData && (
        <Link href={link} className={styles.imageWrapper}>
          <Picture
            src={imageData.src}
            alt={imageData.alt}
            sources={imageData.sources}
            imageClassName={styles.brandImageBg}
          />
        </Link>
      )}
    </aside>
  )
}
